import React, { useState, useEffect, useRef } from 'react';
import './ProjectView.css';
import FileExplorer from './FileExplorer/FileExplorer';
import { getVirtualTourUrl, getVirtualTourCode } from '../../../utils/virtualTourMapping';
import fileService from '../../../services/fileService';
import viewerPresenceService from '../../../services/viewerPresenceService';

const ProjectView = ({ project, projectData, onBack, onViewProject }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [currentPath, setCurrentPath] = useState([{ id: 'root', name: project?.Name || 'Project Files' }]);
  const [hasSelectedFiles, setHasSelectedFiles] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const fileExplorerRef = useRef(null);

  // Update breadcrumb when project changes
  useEffect(() => {
    if (project) {
      setCurrentPath([{ id: 'root', name: project.Name || 'Project Files' }]);
    }
  }, [project]);

  // Check for selected files periodically to update download button visibility
  useEffect(() => {
    const checkSelectedFiles = () => {
      if (fileExplorerRef.current && fileExplorerRef.current.hasSelectedFiles !== undefined) {
        setHasSelectedFiles(fileExplorerRef.current.hasSelectedFiles);
      }
    };

    // Check immediately and then periodically
    checkSelectedFiles();
    const interval = setInterval(checkSelectedFiles, 100);

    return () => clearInterval(interval);
  }, []);

  if (!project) {
    return (
      <div className="project-view-container">
        <div className="project-view-error">
          <h2>Project not found</h2>
          <button onClick={onBack}>Back to Projects</button>
        </div>
      </div>
    );
  }

  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    // Convert to Date object if it's a string
    const dateObj = date instanceof Date ? date : new Date(date);
    
    // Check if the date is valid
    if (isNaN(dateObj.getTime())) return 'N/A';
    
    return dateObj.toLocaleDateString() + ' ' + 
           dateObj.getHours().toString().padStart(2, '0') + ':' + 
           dateObj.getMinutes().toString().padStart(2, '0');
  };

  const isExpired = project.Expiration && new Date(project.Expiration) <= new Date();
  const canView = project.Viewable && !isExpired;
  const hasVirtualTour = project.Code && project.Code !== '';

  const handleFileSelect = (file) => {
    console.log('📁 File selected:', file);
    setSelectedFile(file);
    setSelectedFolder(null);
  };

  const handleFolderSelect = (folder) => {
    console.log('📁 Folder selected:', folder);
    setSelectedFolder(folder);
    setSelectedFile(null);
  };

  const handleBreadcrumbClick = (index, event) => {
    event.stopPropagation(); // Prevent clearing selection when clicking breadcrumbs
    // Navigate to the clicked breadcrumb level
    const newPath = currentPath.slice(0, index + 1);
    setCurrentPath(newPath);
    
    // If clicking on root (index 0), navigate to root folder
    if (index === 0) {
      setSelectedFolder(null);
      // Update URL to project root
      const url = `/projects/${project.Id}`;
      const state = { folderId: 'root' };
      window.history.pushState(state, '', url);
    } else {
      // Find the folder corresponding to the clicked breadcrumb item
      const targetFolderId = newPath[newPath.length - 1].id;
      // We'll need to pass this to FileExplorer to handle the navigation
      setSelectedFolder({ id: targetFolderId, name: newPath[newPath.length - 1].name });
    }
  };

  const updateBreadcrumb = (newPath) => {
    setCurrentPath(newPath);
  };

  const handleClearSelection = () => {
    // This will be passed to FileExplorer to clear file selections
    // The actual implementation is in FileExplorer component
  };

  const handleRefresh = () => {
    // Refresh files via FileExplorer ref
    if (fileExplorerRef.current && fileExplorerRef.current.handleRefresh) {
      fileExplorerRef.current.handleRefresh();
    }
  };

  const handleDownload = () => {
    // Trigger download via FileExplorer ref
    if (fileExplorerRef.current && fileExplorerRef.current.handleDownload) {
      fileExplorerRef.current.handleDownload();
    }
  };

  const handleNewFolder = async () => {
    // Get current folder ID (parent_id)
    // If at root, use project.Id, otherwise use selectedFolder.id
    const parentId = selectedFolder && selectedFolder.id !== 'root' ? selectedFolder.id : project.Id;
    
    // Prompt user for folder name
    const folderName = prompt('Enter folder name:');
    if (!folderName || !folderName.trim()) {
      return; // User cancelled or entered empty name
    }
    
    try {
      // Create the folder
      const response = await fileService.createFolder(project.Id, folderName.trim(), parentId);
      
      // Add folder to current view without full refresh
      if (response.folder && fileExplorerRef.current && fileExplorerRef.current.addFolderToCurrentFolder) {
        fileExplorerRef.current.addFolderToCurrentFolder(response.folder);
      } else {
        // Fallback to refresh if add method not available
        handleRefresh();
      }
    } catch (error) {
      alert(`Error creating folder: ${error.message}`);
      console.error('Error creating folder:', error);
    }
  };

  return (
    <div className="project-view-container">
      {/* Project Header */}
      <div 
        className="project-view-header"
        onClick={handleClearSelection}
      >
        <div className="project-view-header-left">
          <div className="project-view-header-top">
            <button className="back-btn" onClick={onBack} title="Back to Projects">
              <i className="fas fa-arrow-left"></i>
              Back
            </button>
            <span className="project-role-badge">{project.UserRole}</span>
          </div>
          <div className="project-view-header-info">
            <div className="project-info-section">
              <h1 className="project-name">{project.Name}</h1>
              
              <div className="project-details-row">
                <div className="project-meta-info">
                  <span>Project ID: {project.Id}</span>
                  {project.UserRoleId < 2 && (
                    <span>Type: {project.Plan}</span>
                  )}
                  <span>Last Modified: {formatDate(project.Modified)}</span>
                  {project.UserRoleId < 2 && (
                    <span>Expires: {formatDate(project.Expiration)}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="project-actions-header">
          {canView && (
            <button 
              className="header-action-btn primary"
              onClick={async () => {
                console.log(`🎯 3D View button clicked for project ${project.Id}!`);
                // Register overlay click with backend (required for showing user as active)
                try {
                  await viewerPresenceService.registerOverlayClick(project.Id);
                  console.log(`✅ Overlay click registered for project ${project.Id}`);
                } catch (error) {
                  console.warn(`⚠️ Failed to register overlay click for project ${project.Id}:`, error);
                  // Continue with navigation even if registration fails
                }
                // Navigate to 3D view
                onViewProject(project);
              }}
            >
              <i className="fas fa-cube"></i>
              3D View
            </button>
          )}
          
          
          {hasVirtualTour ? (
            <>
              <VirtualTourButton project={project} />
              <button className="header-action-btn tertiary">
                <i className="fas fa-link"></i>
                VR Tour Link
              </button>
            </>
          ) : (
            <>
              <button className="header-action-btn disabled">
                <i className="fas fa-vr-cardboard"></i>
                Virtual Tour
              </button>
              <button className="header-action-btn disabled">
                <i className="fas fa-link"></i>
                VR Tour Link
              </button>
            </>
          )}
          
          <button className="header-action-btn secondary">
            <i className="fas fa-users"></i>
            Project Team
          </button>
          
          <button className="menu-trigger-header">
            <i className="fas fa-ellipsis-v"></i>
          </button>
        </div>
      </div>
      
      {/* Project Content */}
      <div className="project-content">
        {/* Project Data Section */}
        {projectData && (
          <div className="project-data-section">
            <h3>Project Data</h3>
            <div className="project-data-grid">
              <div className="data-item">
                <span className="data-label">Project ID:</span>
                <span className="data-value">{projectData.project_id}</span>
              </div>
              <div className="data-item">
                <span className="data-label">Project Code:</span>
                <span className="data-value">{projectData.project_code}</span>
              </div>
              {projectData.files && projectData.files.length > 0 && (
                <div className="data-item">
                  <span className="data-label">Files:</span>
                  <span className="data-value">{projectData.files.length}</span>
                </div>
              )}
              {projectData.tasks && projectData.tasks.length > 0 && (
                <div className="data-item">
                  <span className="data-label">Tasks:</span>
                  <span className="data-value">{projectData.tasks.length}</span>
                </div>
              )}
              {projectData.reports && projectData.reports.length > 0 && (
                <div className="data-item">
                  <span className="data-label">Reports:</span>
                  <span className="data-value">{projectData.reports.length}</span>
                </div>
              )}
            </div>
          </div>
        )}
        
        <div 
          className="project-view-panel-header"
          onClick={handleClearSelection}
        >
            <div className="breadcrumb-path">
              {currentPath.map((pathItem, index) => (
                <React.Fragment key={pathItem.id}>
                  <span 
                    className="breadcrumb-item"
                    onClick={(e) => handleBreadcrumbClick(index, e)}
                  >
                    {pathItem.name}
                  </span>
                  {index < currentPath.length - 1 && (
                    <i className="fas fa-chevron-right breadcrumb-separator"></i>
                  )}
                </React.Fragment>
              ))}
            </div>
            <div className="breadcrumb-actions">
              {hasSelectedFiles && (
                <button 
                  className="file-action-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload();
                  }}
                  title="Download"
                >
                  <i className="fas fa-download"></i>
                </button>
              )}
              <button 
                className="file-action-btn upload-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowUploadModal(true);
                }}
                title="Upload"
              >
                <i className="fas fa-upload"></i>
              </button>
              <button 
                className="file-action-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNewFolder();
                }}
                title="New Folder"
              >
                <i className="fas fa-folder-plus"></i>
              </button>
              <button 
                className="file-action-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRefresh();
                }}
                title="Refresh"
              >
                <i className="fas fa-sync-alt"></i>
              </button>
            </div>
          </div>
        <div className="project-tree-panel">
            <FileExplorer 
            ref={fileExplorerRef}
            project={project}
            onFileSelect={handleFileSelect}
            onFolderSelect={handleFolderSelect}
            currentPath={currentPath}
            onBreadcrumbUpdate={updateBreadcrumb}
            selectedFolder={selectedFolder}
            onClearSelection={handleClearSelection}
            showUploadModal={showUploadModal}
            onCloseUploadModal={() => setShowUploadModal(false)}
            currentFolderId={selectedFolder && selectedFolder.id !== 'root' ? selectedFolder.id : project.Id}
          />
        </div>
      </div>

    </div>
  );
};

// Virtual Tour Button Component
const VirtualTourButton = ({ project }) => {
  const [virtualTourCode, setVirtualTourCode] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Load virtual tour code on component mount
  useEffect(() => {
    const loadVirtualTourCode = async () => {
      try {
        const code = await getVirtualTourCode(project.Id);
        setVirtualTourCode(code);
      } catch (error) {
        console.error('Error loading virtual tour code:', error);
      }
    };
    
    loadVirtualTourCode();
  }, [project.Id]);

  const handleVirtualTourClick = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      const virtualTourUrl = await getVirtualTourUrl(project.Id);
      console.log('🔗 Opening virtual tour URL:', virtualTourUrl);
      window.open(virtualTourUrl, '_blank');
    } catch (error) {
      console.error('Error getting virtual tour URL:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button 
      className="header-action-btn tertiary"
      onClick={handleVirtualTourClick}
      disabled={isLoading}
      title={virtualTourCode ? `Virtual Tour (${virtualTourCode})` : 'Virtual Tour'}
    >
      <i className="fas fa-vr-cardboard"></i>
      {isLoading ? 'Loading...' : `Virtual Tour ${virtualTourCode ? `(${virtualTourCode})` : ''}`}
    </button>
  );
};

export default ProjectView;
